import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

import PageLayout from "@/components/layout/PageLayout";
import Seo from "@/components/foundation/Seo";
import Container from "@/components/foundation/Container";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import Badge from "@/components/foundation/Badge";
import Button from "@/components/foundation/Button";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import GridPattern from "@/components/foundation/GridPattern";

import ZenScoreGauge from "@/components/marketing/ZenScoreGauge";
import Sparkline from "@/components/marketing/Sparkline";
import MethodologyDiagram from "@/components/marketing/MethodologyDiagram";
import ScoreAnatomy from "@/components/marketing/ScoreAnatomy";
import ScoreBreakdown from "@/components/marketing/ScoreBreakdown";
import ContinuousImprovementCycle from "@/components/marketing/ContinuousImprovementCycle";
import ComingSoonModal from "@/components/marketing/ComingSoonModal";
import { AutoLinkPartners } from "@/components/marketing/PartnerLink";
import {
  Activity,
  Repeat,
  TrendingUp,
  LineChart,
  Calendar,
  AlertTriangle,
} from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/* --------- Expanded signal cards (3-4 sentences each) --------- */
const SIGNALS = [
  {
    icon: Activity,
    title: "Real-Time Cash Flow",
    body:
      "Continuous visibility into every inflow and outflow across your business accounts. ZenScore builds a live model of your cash position — not a snapshot — so your funding readiness reflects how your business is actually operating right now.",
  },
  {
    icon: Repeat,
    title: "Deposit Patterns",
    body:
      "We examine how consistently customer payments arrive, how many distinct sources fund you, and how volatile that mix is week-over-week. Stable, diversified deposits materially improve your readiness signal.",
  },
  {
    icon: TrendingUp,
    title: "Revenue Growth",
    body:
      "Momentum matters. ZenScore looks at month-over-month growth velocity, seasonal trends, and directional signal quality — not just a single revenue number in isolation.",
  },
  {
    icon: LineChart,
    title: "Balance Trends",
    body:
      "Average daily balance is one of the most predictive signals in small-business finance. We track how it trends over time, how often it dips near zero, and how much liquidity headroom you actually operate with.",
  },
  {
    icon: Calendar,
    title: "Business Longevity",
    body:
      "Operating tenure, account maturity, and transaction depth signal how established the underlying business is. Newer businesses can still qualify — ZenScore just weighs the signal appropriately.",
  },
  {
    icon: AlertTriangle,
    title: "Risk Signals",
    body:
      "Overdraft frequency, negative-balance events, volatility spikes, and unusual outflow patterns are all monitored as early-warning indicators. Transparency here means you know exactly what's affecting your score.",
  },
];

const ZS_FAQS = [
  {
    q: "Is ZenScore a credit score?",
    a: "No. ZenScore is a business health and funding readiness indicator built from real financial activity — bank data, deposits, cash flow patterns. It is not a personal or business credit score, and it does not require a credit-bureau pull to compute.",
  },
  {
    q: "Does connecting my bank affect my credit?",
    a: "No. The bank connection is a read-only data link facilitated by Mastercard Finicity. It does not trigger a credit inquiry and has no impact on your personal or business credit profile.",
  },
  {
    q: "How is my score updated?",
    a: "ZenScore updates continuously as new banking activity is received. Meaningful changes — such as a sustained trend improvement or a volatility event — will be reflected in your next refresh.",
  },
  {
    q: "What raises or lowers my score?",
    a: "Consistent revenue, healthy average daily balance, low overdraft frequency, and long operating tenure raise the score. Volatile cash flow, near-zero balances, or missing deposit history tend to lower it. Each factor is visible in your ZenScore breakdown.",
  },
  {
    q: "Can I see the factors driving my score?",
    a: "Yes. Your ZenScore comes with a full breakdown by category (Cash Flow Health, Revenue Consistency, Balance Health, Business Stability) plus the underlying signals. Nothing is hidden.",
  },
  {
    q: "How accurate is it?",
    a: "ZenScore is not a guarantee of funding, but it is a strong predictor of funding readiness because it is built from live financial data rather than historical documents alone. The model improves continuously as our underwriting feedback loops grow.",
  },
];

/* --------- Hero satellite metric cards --------- */
const SATELLITES = [
  {
    label: "Monthly Revenue",
    value: "+18%",
    accent: true,
    data: [14, 16, 18, 22, 26, 32, 34],
    pos: "top-2 -left-24 sm:-left-32",
  },
  {
    label: "Avg Daily Balance",
    value: "Trending Up",
    accent: false,
    data: [22, 24, 24, 26, 28, 31, 33],
    pos: "-bottom-4 -left-16 sm:-left-24",
  },
  {
    label: "Deposit Consistency",
    value: "Excellent",
    accent: true,
    data: [50, 51, 52, 51, 52, 53, 52],
    pos: "top-16 -right-20 sm:-right-32",
  },
  {
    label: "Cash Flow",
    value: "Stable",
    accent: true,
    data: [30, 32, 31, 33, 32, 34, 33],
    pos: "-bottom-2 -right-20 sm:-right-28",
  },
];

export default function ZenScore() {
  const [modal, setModal] = useState({ open: false, source: "zenscore_hero_waitlist" });
  const openModal = (source) => setModal({ open: true, source });
  const setOpen = (open) => setModal((s) => ({ ...s, open }));

  const scrollToMethodology = () => {
    const el = document.getElementById("methodology");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageLayout>
      <Seo
        title="ZenMe Capital · ZenScore — The intelligent business score"
        description="ZenScore is ZenMe's AI-powered funding readiness score, built from real cash flow, deposits, balances, and business signals — not credit history."
        path="/zenscore"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-24"
        data-testid="zenscore-page-hero"
      >
        <GridPattern variant="dots" size={28} opacity={0.4} />
        <GlowOrb
          color="emerald"
          size={620}
          intensity={0.22}
          className="-bottom-40 -left-40"
        />

        <Container className="relative z-10">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <FadeIn>
                <Badge variant="emerald" pulse>ZenScore</Badge>
              </FadeIn>
              <FadeIn delay={0.06}>
                <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
                  The intelligent business score, powered by{" "}
                  <span className="text-brand-emerald">real cash flow.</span>
                </h1>
              </FadeIn>
              <FadeIn delay={0.14}>
                <p className="mt-6 max-w-xl text-base text-text-secondary sm:text-lg leading-relaxed">
                  A proprietary AI-powered funding readiness score, built from
                  your business&rsquo;s real financial activity — not your
                  credit history.
                </p>
              </FadeIn>
              <FadeIn delay={0.22}>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => openModal("zenscore_hero_waitlist")}
                    rightIcon={<ArrowRight className="h-4 w-4" />}
                    data-testid="zenscore-waitlist-btn"
                  >
                    Join the Waitlist
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={scrollToMethodology}
                    leftIcon={<Play className="h-3.5 w-3.5" fill="currentColor" />}
                    data-testid="zenscore-methodology-btn"
                  >
                    See methodology
                  </Button>
                </div>
              </FadeIn>
            </div>

            {/* Gauge + satellites */}
            <div className="relative flex items-center justify-center lg:col-span-6">
              <div className="relative">
                <ZenScoreGauge
                  value={782}
                  size={320}
                  caption="Strong Funding Readiness"
                  data-testid="zenscore-page-gauge"
                />

                {SATELLITES.map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + i * 0.12, duration: 0.6 }}
                    className={`absolute hidden rounded-2xl border border-white/[0.06] bg-bg-card/90 px-4 py-3 backdrop-blur-xl md:flex md:flex-col ${s.pos}`}
                    style={{
                      boxShadow: "0 20px 40px -20px rgba(0,0,0,0.6)",
                    }}
                  >
                    <span className="text-[10px] uppercase tracking-widest text-text-muted">
                      {s.label}
                    </span>
                    <div className="mt-1 flex items-center gap-3">
                      <Sparkline data={s.data} width={60} height={20} />
                      <span
                        className={
                          s.accent
                            ? "text-sm font-medium text-brand-emerald"
                            : "text-sm font-medium text-white"
                        }
                      >
                        {s.value}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* THE PROBLEM WITH TRADITIONAL UNDERWRITING */}
      <Section
        eyebrow="The Gap"
        headline="Credit scores were built for consumers. Businesses deserve better."
        subheadline="Personal credit was designed to answer whether a household would repay a loan. It was never designed to reflect whether a business is generating revenue today, retaining customers, or building momentum."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <FadeIn>
            <Card variant="flat" className="h-full p-8">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted">
                Credit Score
              </span>
              <h3 className="mt-2 font-display text-2xl font-semibold text-white">
                Static · Historical · Consumer-oriented
              </h3>
              <ul className="mt-5 flex flex-col gap-3 text-sm text-text-secondary">
                <li>• Built from historical debt behavior</li>
                <li>• Updates on a monthly reporting cycle</li>
                <li>• Says little about business operations</li>
                <li>• Pulling one leaves a footprint on your credit</li>
              </ul>
            </Card>
          </FadeIn>
          <FadeIn delay={0.1}>
            <Card
              variant="elevated"
              interactive={false}
              className="relative h-full overflow-hidden border-brand-emerald/25 p-8"
            >
              <div
                aria-hidden="true"
                className="absolute inset-x-0 -top-24 h-40 bg-gradient-to-b from-brand-emerald/15 to-transparent"
              />
              <span className="relative font-mono text-[10px] uppercase tracking-[0.16em] text-brand-emerald">
                ZenScore
              </span>
              <h3 className="relative mt-2 font-display text-2xl font-semibold text-white">
                Dynamic · Real-time · Business-oriented
              </h3>
              <ul className="relative mt-5 flex flex-col gap-3 text-sm text-text-secondary">
                <li className="flex gap-2">
                  <span className="text-brand-emerald">›</span> Built from live
                  banking activity
                </li>
                <li className="flex gap-2">
                  <span className="text-brand-emerald">›</span> Refreshes
                  continuously as your business operates
                </li>
                <li className="flex gap-2">
                  <span className="text-brand-emerald">›</span> Reflects revenue
                  momentum, not debt history
                </li>
                <li className="flex gap-2">
                  <span className="text-brand-emerald">›</span> Zero impact on
                  your personal or business credit
                </li>
              </ul>
            </Card>
          </FadeIn>
        </div>
      </Section>

      {/* WHAT ZENSCORE ANALYZES (expanded) */}
      <Section
        eyebrow="What ZenScore Analyzes"
        headline="Six live signals. One composite score."
        subheadline="Each signal is transparently visible in your ZenScore breakdown — no black boxes."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {SIGNALS.map((s, i) => {
            const Icon = s.icon;
            return (
              <FadeIn key={s.title} delay={0.05 * i}>
                <Card
                  variant="elevated"
                  className="flex h-full flex-col gap-4 p-6"
                  data-testid={`zenscore-page-signal-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-brand-emerald/25 bg-brand-emerald/8 text-brand-emerald">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h4 className="font-display text-base font-semibold text-white">
                      {s.title}
                    </h4>
                    <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                      {s.body}
                    </p>
                  </div>
                </Card>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* METHODOLOGY */}
      <div id="methodology" className="scroll-mt-24">
        <Section
          eyebrow="Methodology"
          headline="How ZenScore is calculated."
          subheadline="Five steps translate live banking data into your funding readiness signal."
        >
          <FadeIn>
            <MethodologyDiagram />
          </FadeIn>

          <div className="mx-auto mt-16 max-w-3xl">
            <ol className="flex flex-col gap-6 border-l border-white/[0.08] pl-8">
              {[
                {
                  title: "Data Ingestion",
                  body:
                    "Your business banking data streams into ZenMe via a secure Mastercard Finicity connection. Only read-only access — no credentials stored, no ability to move funds.",
                },
                {
                  title: "Normalization",
                  body:
                    "Raw transactions are cleaned, categorized, deduplicated, and structured into a consistent schema that the model can reason across time.",
                },
                {
                  title: "AI Feature Extraction",
                  body:
                    "Uptiq AI infrastructure extracts higher-order features — trend velocity, volatility, deposit consistency, balance recovery time, and dozens more signals.",
                },
                {
                  title: "Scoring Model",
                  body:
                    "The ZenScore model combines those features into four category sub-scores (Cash Flow Health, Revenue Consistency, Balance Health, Business Stability) and a composite score from 0 to 1000.",
                },
                {
                  title: "ZenScore Output",
                  body:
                    "Your live ZenScore, category breakdowns, and funding readiness commentary are surfaced in the app — updated as new data flows in.",
                },
              ].map((s, i) => (
                <FadeIn key={s.title} delay={0.05 * i}>
                  <li className="relative">
                    <span className="absolute -left-[41px] top-1 flex h-6 w-6 items-center justify-center rounded-full border border-brand-emerald/40 bg-bg-base font-mono text-[10px] font-semibold text-brand-emerald">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h4 className="font-display text-lg font-semibold text-white">
                      {s.title}
                    </h4>
                    <p className="mt-1.5 text-sm leading-relaxed text-text-secondary">
                      <AutoLinkPartners text={s.body} />
                    </p>
                  </li>
                </FadeIn>
              ))}
            </ol>
          </div>
        </Section>
      </div>

      {/* SCORE BREAKDOWN — 7 real signals from ZenScore methodology */}
      <Section
        eyebrow="Score Breakdown"
        headline="Every score is broken down into 7 signals."
        subheadline="ZenScore is composed of seven weighted signals derived from real business financial activity. Each signal contributes a different weight toward the overall score."
      >
        <ScoreBreakdown />
      </Section>

      {/* SCORE ANATOMY */}
      <Section
        eyebrow="Sample Score Anatomy"
        headline="Every score is fully broken down."
        subheadline="See exactly which categories are driving your composite ZenScore — no black boxes, no mystery."
      >
        <FadeIn>
          <ScoreAnatomy />
        </FadeIn>
      </Section>

      {/* CONTINUOUS IMPROVEMENT */}
      <Section
        eyebrow="Continuous Improvement"
        headline="A model that keeps learning."
        subheadline="Every underwriting outcome, repayment signal, and new merchant profile refines the ZenScore model over time. What you see today is a starting point — not a ceiling."
      >
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="flex justify-center lg:col-span-5">
            <FadeIn>
              <ContinuousImprovementCycle size={360} />
            </FadeIn>
          </div>
          <div className="lg:col-span-7">
            <ul className="flex flex-col gap-6">
              {[
                {
                  title: "Feedback loops",
                  body:
                    "Repayment behavior, partner underwriting decisions, and outcome data flow back into model training pipelines.",
                },
                {
                  title: "Better signals over time",
                  body:
                    "The set of features the model considers grows and refines as we observe how real businesses perform.",
                },
                {
                  title: "Transparent versioning",
                  body:
                    "Score methodology updates are versioned and documented. Merchants and partners can trace how the model has evolved.",
                },
              ].map((s, i) => (
                <FadeIn key={s.title} delay={0.05 * i}>
                  <li className="flex items-start gap-4">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-emerald shadow-[0_0_8px_rgba(0,230,118,0.7)]" />
                    <div>
                      <h4 className="font-display text-lg font-semibold text-white">
                        {s.title}
                      </h4>
                      <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                        {s.body}
                      </p>
                    </div>
                  </li>
                </FadeIn>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ZENSCORE FAQ */}
      <Section
        eyebrow="ZenScore FAQ"
        headline="Straight answers."
        subheadline="If we haven't covered your question, get in touch — we'll answer directly."
      >
        <FadeIn>
          <Accordion
            type="single"
            collapsible
            className="mx-auto w-full max-w-3xl divide-y divide-white/[0.06] overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.01]"
            data-testid="zenscore-faq-accordion"
          >
            {ZS_FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`zs-${i}`}
                className="border-b-0 px-6 first:pt-2 last:pb-2"
              >
                <AccordionTrigger className="py-5 text-left text-base font-medium text-white hover:no-underline data-[state=open]:text-brand-emerald sm:text-lg">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 pr-8 text-sm leading-relaxed text-text-secondary">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeIn>
      </Section>

      {/* CTA BAND */}
      <section
        className="relative overflow-hidden py-24 sm:py-32"
        data-testid="zenscore-cta-band"
      >
        <GlowOrb
          color="emerald"
          size={620}
          intensity={0.22}
          className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
        <Container className="relative z-10">
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 text-center">
            <FadeIn>
              <h2 className="font-display text-4xl font-semibold text-white sm:text-5xl">
                Ready to see your ZenScore?
              </h2>
            </FadeIn>
            <FadeIn delay={0.08}>
              <p className="max-w-xl text-base text-text-secondary sm:text-lg">
                Join the waitlist to generate your score at launch, or explore
                how funding partners use ZenScore intelligence.
              </p>
            </FadeIn>
            <FadeIn delay={0.16}>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => openModal("zenscore_cta_waitlist")}
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                >
                  Join the Waitlist
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => (window.location.href = "/partners")}
                >
                  See how partners use it
                </Button>
              </div>
            </FadeIn>
          </div>
        </Container>
      </section>

      <ComingSoonModal
        open={modal.open}
        onOpenChange={setOpen}
        source={modal.source}
      />
    </PageLayout>
  );
}
